import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';

import { AbstractMethodComponent } from '../abstract-method.component';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { CREDIT_CARD_DEFAULT_STYLES, CreditCardStyles } from '../../payment-methods.model';
import { OrderFormValidationResult } from '@orderform/widgets';

declare let braintree: any;

@Component({
  selector: 'orderform-payment-method-braintree',
  templateUrl: './braintree.component.html',
  styleUrls: ['./braintree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BraintreeComponent),
      multi: true,
    },
  ],
})
export class BraintreeComponent
  extends AbstractMethodComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit
{
  @Input() config: {
    jsSdkUrls: string[];
    sdkType: 'hostedFields' | 'dropin';
    clientToken: string;
    locale: string;
    has3dSecure: boolean;
    debugLabel: string;
    method: 'creditcard';
    config: any;
    clientIp: string;
  };
  @Input() styles?: CreditCardStyles = CREDIT_CARD_DEFAULT_STYLES;
  private _subscriptionBeforeSubmit = this._orderformFascade.beforeSubmit.subscribe(() => this._onBeforeSubmit());
  private _subscriptionFormValidation = this._orderformFascade.formValidation.subscribe(
    () => this._onFormValidation(),
    this._element
  );

  private _braintreeDropinInstance: any;

  private _waitForLoad: Promise<void>;

  public creditCardType$ = new Subject<string>();
  public successByField$ = {
    number: new BehaviorSubject<boolean>(false),
    expirationDate: new BehaviorSubject<boolean>(false),
    cvv: new BehaviorSubject<boolean>(false),
  };
  public errorByField$ = {
    number: new BehaviorSubject<string>(''),
    expirationDate: new BehaviorSubject<string>(''),
    cvv: new BehaviorSubject<string>(''),
  };
  public touchedByField$ = {
    number: new BehaviorSubject<boolean>(false),
    expirationDate: new BehaviorSubject<boolean>(false),
    cvv: new BehaviorSubject<boolean>(false),
  };
  private _braintreeHostedFieldsInstance: any;
  private _braintreeThreeDSecureInstance: any;

  constructor(private _orderformFascade: OrderformFascade, private _ngZone: NgZone, injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    this._waitForLoad = this._load();
    this._debugLabel = this.config.debugLabel;
  }

  private async _load(): Promise<void> {
    await this._loadExternalResources(this.config.jsSdkUrls);
  }

  public async ngAfterViewInit() {
    await this._waitForLoad;

    if (this.config.sdkType === 'hostedFields') {
      await this._createHostedFieldsInstance();
    } else {
      this._createDropinInstance();
    }
  }

  private async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }

    await this._waitForLoad;

    if (this.config.sdkType === 'hostedFields') {
      return await this._onBeforeSubmitHostedFields();
    } else {
      return await this._onBeforeSubmitDropin();
    }
  }
  private async _onBeforeSubmitDropin(): Promise<{ valid: boolean }> {
    const requestPaymentConfig: { threeDSecure?: any } = {};
    if (this.config.has3dSecure) {
      const orderData = await firstValueFrom(this._orderformFascade.order$);

      const helperGetDataForAddressType = (isBilling: boolean) => {
        const clone = {
          givenName: 'buyer_address_first_name',
          surname: 'buyer_address_last_name',
          phoneNumber: 'buyer_address_phone_no',
          streetAddress: 'buyer_address_street',
          extendedAddress: 'buyer_address_street_number',
          locality: 'buyer_address_city',
          region: 'buyer_address_state',
          postalCode: 'buyer_address_zipcode',
          countryCodeAlpha2: 'buyer_address_country',
        };
        Object.keys(clone).forEach((key) => {
          if (isBilling) {
            clone[key] = orderData.order.billing_address[clone[key] + '_billing'];
          } else {
            clone[key] = orderData.order.shipping_address[clone[key]];
          }
        });
        return clone;
      };

      const shippingAddress = helperGetDataForAddressType(false);
      // Check is billing address active
      // if not, set billingAddress data to empty object because default would be the shipping address
      const billingAddress = orderData.order.has_billing_address ? helperGetDataForAddressType(true) : ({} as any);

      requestPaymentConfig.threeDSecure = {
        amount: Math.max(1.0, orderData.order_summary.order_final.gross_1st),
        email: orderData.order.shipping_address.buyer_address_email,
        billingAddress: {
          givenName: billingAddress.givenName || shippingAddress.givenName || '',
          surname: billingAddress.surname || shippingAddress.surname || '',
          phoneNumber: billingAddress.phoneNumber || shippingAddress.phoneNumber || '',
          streetAddress: billingAddress.streetAddress || shippingAddress.streetAddress,
          extendedAddress: billingAddress.extendedAddress || shippingAddress.extendedAddress || '',
          locality: billingAddress.locality || shippingAddress.locality || '',
          region: billingAddress.region || shippingAddress.region || '',
          postalCode: billingAddress.postalCode || shippingAddress.postalCode || '',
          countryCodeAlpha2: billingAddress.countryCodeAlpha2 || shippingAddress.countryCodeAlpha2 || '',
        },
        additionalInformation: {
          workPhoneNumber: shippingAddress.phoneNumber || '',
          shippingGivenName: shippingAddress.givenName || '',
          shippingSurname: shippingAddress.surname || '',
          shippingPhone: shippingAddress.phoneNumber || '',
          shippingAddress: {
            streetAddress: shippingAddress.streetAddress || '',
            extendedAddress: shippingAddress.extendedAddress || '',
            locality: shippingAddress.locality || '',
            region: shippingAddress.region || '',
            postalCode: shippingAddress.postalCode || '',
            countryCodeAlpha2: shippingAddress.countryCodeAlpha2 || '',
          },
          ipAddress: this.config.clientIp,
        },
        collectDeviceData: true,
      };
    }

    return this._braintreeDropinInstance
      .requestPaymentMethod(requestPaymentConfig)
      .then((payload) => {
        if (payload && typeof payload.nonce != 'undefined') {
          const device_data = typeof payload.deviceData === 'string' ? payload.deviceData.replace(/\"/g, '::Q::') : '';

          this._paymentDataChanged.next({
            valid: true,
            braintree_nonce: payload.nonce,
            braintree_device: device_data,
          });
          return { valid: true };
        } else {
          this._paymentDataChanged.next({
            valid: false,
          });
          return { valid: false };
        }
      })
      .catch((error) => {
        console.log('DS24-PP $id: payment error: ', error);
        const errorElement = document.querySelector('.braintree-sheet__error-text');
        const isErrorElementVisible = errorElement && errorElement.getBoundingClientRect().height > 0;
        if (!isErrorElementVisible) {
          this._toaster.error(error.message);
        }
        this._monitoring.trackValidationError(
          'exception:' + error.message,
          'frontend',
          'ds-orderform-payment-braintree'
        );
        this._paymentDataChanged.next({
          valid: false,
        });
        return { valid: false };
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._subscriptionBeforeSubmit.unsubscribe();
    this._subscriptionFormValidation.unsubscribe();
  }

  private async _onBeforeSubmitHostedFields(): Promise<{ valid: boolean }> {
    try {
      const payload = await this._braintreeHostedFieldsInstance.tokenize();
      let nonce = payload.nonce;
      let deviceData = payload.deviceData;
      if (this.config.has3dSecure) {
        const orderData = await firstValueFrom(
          this._orderformFascade.order$
            .pipe
            //map((order) => order)
            ()
        );
        const hasBillingAddress = orderData.order.has_billing_address;
        const addressData = hasBillingAddress ? orderData.order.billing_address : orderData.order.shipping_address;

        const threeDSecurePayload = await this._braintreeThreeDSecureInstance.verifyCard({
          onLookupComplete: (data: any, next: () => void) => {
            next();
          },
          nonce: payload.nonce,
          bin: payload.details.bin,
          amount: Math.max(1.0, orderData.order_summary.order_final.gross_1st),
          email: orderData.order.shipping_address.buyer_address_email,
          billingAddress: {
            givenName: addressData.buyer_address_first_name || '',
            surname: addressData.buyer_address_last_name || '',
            phoneNumber: orderData.order.shipping_address.buyer_address_phone_no || '',
            streetAddress:
              (addressData.buyer_address_street || '') + ' ' + (addressData.buyer_address_street_number || ''),
            locality: addressData.buyer_address_city || '',
            region: addressData.buyer_address_state || '',
            postalCode: addressData.buyer_address_zipcode || '',
            countryCodeAlpha2: addressData.buyer_address_country || '',
          },
          additionalInformation: {
            ipAddress: this.config.clientIp,
          },
          collectDeviceData: true,
        });

        if (!threeDSecurePayload.liabilityShifted) {
          const liabilityError = new Error('Liability did not shift');
          //liabilityError.payload = threeDSecurePayload;
          throw liabilityError;
        }

        nonce = threeDSecurePayload.nonce;
        deviceData = threeDSecurePayload.deviceData;
      }

      const device_data = typeof deviceData != 'undefined' ? deviceData.replace(/\"/g, '::Q::') : '';

      this._paymentDataChanged.next({
        valid: true,
        braintree_nonce: nonce,
        braintree_device: device_data,
      });
      return { valid: true };
    } catch (error) {
      console.log('DS24-PP $id: payment error: ', error);

      if (error?.details?.invalidFieldKeys.length > 0) {
        error?.details?.invalidFieldKeys.forEach((fieldKey) => {
          this.successByField$[fieldKey].next(false);
          this.errorByField$[fieldKey].next($localize`:@@payment_method.error.invalid_input:Ungültige Eingabe.`);
          this._monitoring.trackValidationError(fieldKey + ':invalid', 'frontend', 'ds-orderform-payment-braintree');
        });
      } else {
        this._toaster.error(error.message);
        this._monitoring.trackValidationError(
          'exception:' + error.message,
          'frontend',
          'ds-orderform-payment-braintree'
        );
      }

      this._paymentDataChanged.next({
        valid: false,
      });
      return { valid: false };
    }
  }

  private async _createDropinInstance() {
    braintree.dropin.create(
      {
        ...this.config.config,
        authorization: this.config.clientToken,
        container: '#braintree-container-' + this.uniqueInstanceId,
        locale: this.config.locale,
        fields: {
          number: {
            container: '#braintree-card-number-' + this.uniqueInstanceId,
            placeholder: '4444 3333 2222 1111',
          },
          cvv: {
            container: '#braintree-cvv-' + this.uniqueInstanceId,
            placeholder: '•••',
          },
          expirationDate: {
            container: '#braintree-expiration-date-' + this.uniqueInstanceId,
            placeholder: '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2),
          },
        },
      },
      (error, dropinInstance) => {
        if (!dropinInstance) {
          this._hidePaymentMethodWithError(error);
          return;
        }
        this._braintreeDropinInstance = dropinInstance;
      }
    );
  }

  private async _createHostedFieldsInstance() {
    try {
      this._braintreeHostedFieldsInstance = await braintree.hostedFields.create({
        authorization: this.config.clientToken,
        locale: this.config.locale,
        fields: {
          number: {
            container: '#braintree-card-number-' + this.uniqueInstanceId,
            placeholder: '4444 3333 2222 1111',
          },
          cvv: {
            container: '#braintree-cvv-' + this.uniqueInstanceId,
            placeholder: '●●●',
          },
          expirationDate: {
            container: '#braintree-expiration-date-' + this.uniqueInstanceId,
            placeholder: '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2),
          },
        },
        styles: {
          'input,select': {
            'font-size': parseFloat(this.styles.fontSize) * 16 + 'px',
            color: this.styles.textColor,
          },

          'input:-webkit-autofill, input::placeholder': {
            color: this.styles.placeholderColor,
          },
        },
      });

      this._braintreeHostedFieldsInstance.on('cardTypeChange', (e) => {
        this._ngZone.run(() => {
          if (e.cards.length === 1) {
            this.creditCardType$.next(e.cards[0].type);
          } else {
            this.creditCardType$.next('');
          }
        });
      });

      this._braintreeHostedFieldsInstance.on('blur', (event) => {
        this._ngZone.run(() => {
          this.touchedByField$[event.emittedBy].next(true);
        });
      });

      this._braintreeHostedFieldsInstance.on('validityChange', (event) => {
        this._ngZone.run(() => {
          const field = event.fields[event.emittedBy];
          this.touchedByField$[event.emittedBy].next(true);
          if (this.successByField$[event.emittedBy]) {
            this.successByField$[event.emittedBy].next(field.isValid);
            if (!field.isValid) {
              this.errorByField$[event.emittedBy].next(
                $localize`:@@payment_method.error.invalid_input:Ungültige Eingabe.`
              );
            } else {
              this.errorByField$[event.emittedBy].next('');
            }
          }
        });
      });

      if (this.config.has3dSecure) {
        this._braintreeThreeDSecureInstance = await braintree.threeDSecure.create({
          authorization: this.config.clientToken,
          locale: this.config.locale,
          version: 2,
        });
      }
    } catch (err) {
      this._hidePaymentMethodWithError(err);
    }
  }

  private _onFormValidation(): Promise<OrderFormValidationResult> {
    if (!this.isActive()) {
      return Promise.resolve({
        valid: true,
        component: 'ds-orderform-payment-braintree',
        invalidFieldList: [],
      });
    }

    const invalidFieldList = Object.keys(this.errorByField$)
      .filter((field) => this.errorByField$[field].value)
      .map((field) => ({
        id: field,
        errors: [this.errorByField$[field].value],
      }));
    return Promise.resolve({
      valid: invalidFieldList.length === 0,
      component: 'ds-orderform-payment-braintree',
      invalidFieldList,
    });
  }
}
